/**
 * RTWO Cryptography & Identity Engine — SHA-256 Hash Provider
 *
 * Truthful hashing only — no fabricated digests.
 */

import { sha256 } from '@noble/hashes/sha2.js';
import type { MessageHashHex } from '../../domain-models';
import type { HashAlgorithm, IHashProvider } from '../../adapters/hash-provider-interface';
import { bytesToHex, hexToBytes, isValidHex } from './crypto-utils';

export class Sha256HashProvider implements IHashProvider {
  readonly providerId = 'sha256-hash-provider';
  readonly providerName = 'SHA-256 Hash Provider';
  readonly algorithm: HashAlgorithm = 'SHA-256';

  private configured = false;

  configure(): void {
    this.configured = true;
  }

  isReady(): boolean {
    return this.configured;
  }

  hashBytes(data: Uint8Array): MessageHashHex {
    this.requireReady();
    return bytesToHex(sha256(data));
  }

  hashUtf8(text: string): MessageHashHex {
    this.requireReady();
    return this.hashBytes(new TextEncoder().encode(text));
  }

  hashHex(hex: string): MessageHashHex {
    this.requireReady();
    if (!isValidHex(hex)) {
      throw new Error('Invalid hex input for SHA-256 hash.');
    }
    return this.hashBytes(hexToBytes(hex));
  }

  private requireReady(): void {
    if (!this.configured) {
      throw new Error('SHA-256 hash provider is not configured.');
    }
  }
}
